import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './Dropdown.css';
import LoadingOverlay from '../components/LoadingOverlay';
import FormularioAgendamento from '../Formulario/FormularioAgendamento';
import { getAuthToken } from '../utils/auth';
import BASE_URL from '../utils/api';
import { ehPrincipal } from '../utils/responsavelPrincipal';

// Helper: listens for MobileFooter generic + event
function MobileAddListener({ onOpen }) {
  useEffect(() => {
    window.addEventListener('open-mobile-add-popup', onOpen);
    return () => window.removeEventListener('open-mobile-add-popup', onOpen);
  }, [onOpen]);
  return null;
}

// Função utilitária para fazer chamadas da API
const apiCall = async (endpoint) => {
  const urls = [
    `${BASE_URL}/${endpoint}`,
    `${BASE_URL}/${endpoint}`,
  ];

  const headers = {};
  const token = getAuthToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  for (const url of urls) {
    try {
      const response = await fetch(url, { headers });
      if (response.ok) return await response.json();
    } catch (error) {
      // continua para a próxima URL
    }
  }

  throw new Error('Backend indisponível.');
};

/*
// Componente de formulário de agendamento (não utilizado atualmente)
function AgendamentoFormulario({ idCliente, idOportunidade, onClose }) {
  return (
    <div className="popup-agendamento-overlay" onClick={onClose}>
      <div className="popup-agendamento" onClick={e => e.stopPropagation()}>
        <div className="popup-agendamento-centro">
          <div className="popup-titulo-laranja">NOVO AGENDAMENTO</div>
          <form style={{ display: 'flex', flexDirection: 'column', gap: 18, alignItems: 'center', width: '100%' }}>
            <div style={{ width: '100%' }}>
              <label style={{ color: 'rgb(235, 133, 0)', fontWeight: 'bold' }}>ID do Cliente</label>
              <input
                type="text"
                value={idCliente}
                readOnly
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '5px',
                  border: '1px solid #ccc',
                  background: '#f0f0f0',
                  marginBottom: '16px'
                }}
              />
            </div>
            <div style={{ width: '100%' }}>
              <label style={{ color: 'rgb(235, 133, 0)', fontWeight: 'bold' }}>ID da Oportunidade</label>
              <input
                type="text"
                value={idOportunidade}
                readOnly
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '5px',
                  border: '1px solid #ccc',
                  background: '#f0f0f0'
                }}
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
*/

// Adicione este componente utilitário no topo do arquivo (após os imports)
function BotaoVoltarPopup({ onClick }) {
  return (
    <button className="popup-voltar-btn" onClick={onClick} aria-label="Voltar">
      {/* SVG de seta para a esquerda */}
      <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
        <path d="M13 16L8 11L13 6" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </button>
  );
}

export default function AgendamentosDropdown() {
  const location = useLocation();
  const [mostrarPopup, setMostrarPopup] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [popupOportunidades, setPopupOportunidades] = useState(false);
  const [oportunidades, setOportunidades] = useState([]);
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [formAgendamento, setFormAgendamento] = useState({ aberto: false, idCliente: null, idOportunidade: null, agendamento: null });
  const [agendamentos, setAgendamentos] = useState([]);
  const [todasOportunidades, setTodasOportunidades] = useState([]);
  const [todosClientes, setTodosClientes] = useState([]);
  const [tiposAgendamento, setTiposAgendamento] = useState([]);
  const [responsaveis, setResponsaveis] = useState([]);
  // { agendamentoId: [responsáveis] }
  const [responsaveisPorAgendamento, setResponsaveisPorAgendamento] = useState({});
  // Estado de loading para exibir o overlay
  const [loading, setLoading] = useState(false);
  const [erroCarregamento, setErroCarregamento] = useState(null);

  useEffect(() => {
    const carregarDadosIniciais = async () => {
      setLoading(true);
      try {
        const [clientesResp, pessoasResp] = await Promise.all([
          apiCall('Cliente'),
          apiCall('Pessoas')
        ]);
        console.log('Clientes carregados:', clientesResp);
        console.log('Pessoas carregadas:', pessoasResp);
        setClientes(clientesResp || []);
        setResponsaveis(pessoasResp || []);
      } catch (error) {
        console.error('Erro ao carregar dados iniciais:', error);
        setClientes([]);
        setResponsaveis([]);
      } finally {
        setLoading(false);
      }
    };
    carregarDadosIniciais();
  }, []);

  // Listener para abrir formulário via tour guiado
  useEffect(() => {
    const abrirFormularioTour = () => {
      setMostrarPopup(false);
      setPopupOportunidades(false);
      setFormAgendamento({ aberto: true, idCliente: null, idOportunidade: null, agendamento: null });
    };
    const fecharFormularioTour = () => {
      setFormAgendamento({ aberto: false, idCliente: null, idOportunidade: null, agendamento: null });
    };
    window.addEventListener('abrir-formulario-agendamento-tour', abrirFormularioTour);
    window.addEventListener('fechar-formulario-agendamento-tour', fecharFormularioTour);
    return () => {
      window.removeEventListener('abrir-formulario-agendamento-tour', abrirFormularioTour);
      window.removeEventListener('fechar-formulario-agendamento-tour', fecharFormularioTour);
    };
  }, []);

  // If navigated with an editAgendamentoId in location.state, fetch that agendamento and open edit form
  useEffect(() => {
    const editId = location.state && (location.state.editAgendamentoId || location.state.editAgendamentoId === 0) ? location.state.editAgendamentoId : null;
    if (!editId) return;
    const fetchAndOpen = async () => {
      try {
        setLoading(true);
        const ag = await apiCall(`Agendamentos/${editId}`);
        if (!ag) return;
        const oportunidadeId = ag.OportunidadeId || ag.oportunidadeId;
        let oportunidade = null;
        if (oportunidadeId) {
          try { oportunidade = await apiCall(`Oportunidade/${oportunidadeId}`); } catch (e) { oportunidade = null; }
        }
        setFormAgendamento({
          aberto: true,
          idAgendamento: ag.Id || ag.id,
          idCliente: ag.clienteId || ag.ClienteId || ag.Id_cliente || ag.IdCliente || ag.Cliente || ag.idCliente || null,
          idOportunidade: oportunidadeId || (oportunidade && (oportunidade.Id || oportunidade.id)),
          oportunidadeCompleta: oportunidade
        });
        setMostrarPopup(false);
        setPopupOportunidades(false);
      } catch (err) {
        console.error('Erro ao abrir agendamento por id passado via navegação', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAndOpen();
  }, [location.state && location.state.editAgendamentoId]);

  // Busca oportunidades filtradas pelo cliente selecionado ao abrir o popup de oportunidades
  useEffect(() => {
    if (popupOportunidades && clienteSelecionado) {
      const fetchOportunidades = async () => {
        try {
          const clienteId = clienteSelecionado.id || clienteSelecionado.Id;
          console.log(`🔍 Buscando oportunidades para cliente ID: ${clienteId}...`);
          
          const todasOportunidades = await apiCall('Oportunidade');
          
          // Filtrar oportunidades apenas do cliente selecionado
          const oportunidadesFiltradas = todasOportunidades.filter(op => 
            (op.clienteId || op.ClienteId) === clienteId
          );
          
          console.log(`✅ Oportunidades do cliente ${clienteId} carregadas:`, oportunidadesFiltradas?.length || 0);
          setOportunidades(oportunidadesFiltradas || []);
        } catch (error) {
          console.error('Erro ao buscar oportunidades:', error);
          setOportunidades([]);
        }
      };

      fetchOportunidades();
    }
  }, [popupOportunidades, clienteSelecionado]);

  // Adicione os hooks para a listagem:
  useEffect(() => {
    const fetchAgendamentos = async () => {
      try {
        const data = await apiCall('Agendamentos');
        setAgendamentos(data || []);
        setErroCarregamento(null);
        // Responsáveis de cada agendamento (sem principal e com vários, o card mostra a quantidade)
        try {
          const resp = await apiCall('Responsaveis');
          const porAgendamento = {};
          (Array.isArray(resp) ? resp : []).forEach(r => {
            const agId = r.agendamentoId ?? r.AgendamentoId;
            if (agId != null) (porAgendamento[agId] = porAgendamento[agId] || []).push(r);
          });
          setResponsaveisPorAgendamento(porAgendamento);
        } catch {
          setResponsaveisPorAgendamento({});
        }
      } catch (error) {
        setErroCarregamento('Não foi possível conectar ao servidor. Verifique se o backend está rodando.');
        setAgendamentos([]);
      }
    };

    fetchAgendamentos();
  }, [formAgendamento.aberto]); // Atualiza sempre que o popup de formulário fecha

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Buscar todas as oportunidades
        const oportunidadesData = await apiCall('Oportunidade');
        setTodasOportunidades(oportunidadesData);

        // Buscar todos os clientes
        const clientesData = await apiCall('Cliente');
        setTodosClientes(clientesData);

        // Buscar tipos de agendamento
        try {
          const tiposData = await apiCall('TipoAgendamento');
          setTiposAgendamento(tiposData);
        } catch (error) {
          console.error('Tipos de agendamento não encontrados:', error);
          setTiposAgendamento([]);
        }
      } catch (error) {
        console.error('Erro ao buscar dados:', error);
        setTodosClientes([]);
        setTodasOportunidades([]);
        setTiposAgendamento([]);
      }
    };

    fetchData();
  }, []);

  const getNomeCliente = (oportunidadeId) => {
    const oportunidade = todasOportunidades.find(o => (o.id || o.Id) === oportunidadeId);
    if (!oportunidade) return 'Cliente não encontrado';
    const clienteId = oportunidade.clienteId || oportunidade.ClienteId;
    const cliente = todosClientes.find(c => (c.id || c.Id) === clienteId);
    return cliente ? (cliente.nome || cliente.Nome) : 'Cliente não encontrado';
  };

  // Extract client name directly from agendamento's Cliente navigation property
  const getNomeClienteFromAgendamento = (ag) => {
    if (!ag) return '';
    const clienteObj = ag.Cliente || ag.cliente;
    if (clienteObj && typeof clienteObj === 'object') {
      return clienteObj.Nome || clienteObj.nome || clienteObj.NomeCliente || clienteObj.name || '';
    }
    // fallback to oportunidade lookup
    const oportunidadeId = ag.OportunidadeId || ag.oportunidadeId;
    if (oportunidadeId) return getNomeCliente(oportunidadeId);
    return '';
  };

  // Extract responsavel name from agendamento's Pessoa navigation property
  // Card: o responsável principal; sem principal e com vários responsáveis, "N responsáveis"
  const getResponsavelFromAgendamento = (ag) => {
    if (!ag) return '-';
    const doAgendamento = responsaveisPorAgendamento[ag.id ?? ag.Id] || [];
    if (doAgendamento.length > 1 && !doAgendamento.some(ehPrincipal)) return `${doAgendamento.length} responsáveis`;
    const pessoaObj = ag.Pessoa || ag.pessoa;
    if (pessoaObj && typeof pessoaObj === 'object') {
      return pessoaObj.Nome || pessoaObj.nome || pessoaObj.NomeCompleto || pessoaObj.nomeCompleto || pessoaObj.name || '-';
    }
    // fallback to responsaveis lookup
    const responsavelId = ag.PessoasId || ag.pessoasId || ag.PessoaId || ag.pessoaId || ag.ResponsavelId || ag.responsavelId;
    const responsavelObj = responsaveis.find(r => (r.id || r.Id || r.PessoasId || r.pessoasId) === responsavelId);
    if (responsavelObj) {
      return responsavelObj.nome || responsavelObj.Nome || responsavelObj.NomeCompleto || '-';
    }
    return ag.PessoasNome || ag.PessoaNome || ag.PessoaNomeCompleto || ag.Responsavel || ag.responsavel || '-';
  };

  const getTelefoneCliente = (idCliente) => {
    const cliente = todosClientes.find(c => (c.id || c.Id) === idCliente);
    return cliente ? (cliente.telefone || cliente.Telefone || 'Sem telefone') : 'Sem telefone';
  };

  const getCpfCnpjCliente = (idCliente) => {
    const cliente = todosClientes.find(c => (c.id || c.Id) === idCliente);
    return cliente ? (cliente.cpfCnpj || cliente.CpfCnpj || 'Sem CPF/CNPJ') : 'Sem CPF/CNPJ';
  };

  const getNomeTipoAgendamento = (tipoAgendamentoId) => {
    const tipo = tiposAgendamento.find(t => (t.id || t.Id) === tipoAgendamentoId);
    return tipo ? (tipo.nome || tipo.Nome || 'Tipo não encontrado') : 'Tipo não encontrado';
  };

  // Função para fechar todos os popups
  const fecharTodosPopups = () => {
    setMostrarPopup(false);
    setPopupOportunidades(false);
    setClienteSelecionado(null);
    setFormAgendamento({ aberto: false, idCliente: null, idOportunidade: null, agendamento: null });
  };

  // Notifica AjudaWidget do estado dos popups de agendamento
  useEffect(() => {
    const formularioAberto = formAgendamento.aberto;
    const outroPopupAberto = mostrarPopup || popupOportunidades;
    window.dispatchEvent(new CustomEvent('agendamento-popup-state', {
      detail: { formularioAberto, outroPopupAberto }
    }));
    return () => window.dispatchEvent(new CustomEvent('agendamento-popup-state', {
      detail: { formularioAberto: false, outroPopupAberto: false }
    }));
  }, [formAgendamento.aberto, mostrarPopup, popupOportunidades]);

  // Recebe pedido de fechar todos os popups do AjudaWidget
  useEffect(() => {
    const handler = () => fecharTodosPopups();
    window.addEventListener('agendamento-fechar-todos-popups', handler);
    return () => window.removeEventListener('agendamento-fechar-todos-popups', handler);
  }, []);

  // Filtros
  const [filtro, setFiltro] = useState('');
  // campoFiltro: 'nome' | 'idAgendamento' | 'id' | 'responsavel'
  const [tipoFiltro, setTipoFiltro] = useState('nome');
  const [dropdownFiltroAberto, setDropdownFiltroAberto] = useState(false);
  // tipoAgendamentoFiltro: '' (todos) | string com id do tipo
  const [tipoAgendamentoFiltro, setTipoAgendamentoFiltro] = useState('');
  const [dropdownTipoAberto, setDropdownTipoAberto] = useState(false);
  // Ao entrar na tela mostra sempre TODOS os agendamentos; "atrasados" só quando a pessoa escolher na lista
  const [mostrarAtrasados, setMostrarAtrasados] = useState(false);
  const [dropdownAtrasadosAberto, setDropdownAtrasadosAberto] = useState(false);

  // Helper: determina se um agendamento está atrasado
  const isAgendamentoAtrasado = (ag) => {
    const dataHoraFormatada = ag.DataHora || ag.dataHora;
    if (!dataHoraFormatada) return false;
    let data = '', horaInicio = '';
    let dh = dataHoraFormatada;
    if (dh.includes('T')) dh = dh.replace('T', ' ');
    [data, horaInicio] = dh.split(' ');
    if (!data) return false;
    const agendamentoDate = new Date(`${data}T${horaInicio || '00:00'}`);
    const now = new Date();
    return now > agendamentoDate;
  };

  // Filtro aplicado nos agendamentos
  const agendamentosFiltrados = agendamentos.filter(ag => {
    // Filtro por tipo de agendamento
    if (tipoAgendamentoFiltro) {
      const tipoAg = ag.TipoAgendamentoId || ag.tipoAgendamentoId;
      if (tipoAg == null || String(tipoAg) !== tipoAgendamentoFiltro) return false;
    }
    // Filtro de texto por campo
    if (filtro) {
      if (tipoFiltro === 'idAgendamento') {
        if (!String(ag.Id || ag.id || '').includes(filtro)) return false;
      } else if (tipoFiltro === 'id') {
        if (!String(ag.OportunidadeId || ag.oportunidadeId || '').includes(filtro)) return false;
      } else if (tipoFiltro === 'responsavel') {
        if (!getResponsavelFromAgendamento(ag).toLowerCase().includes(filtro.toLowerCase())) return false; // responsável principal (o do card)
      } else {
        const oportunidadeId = ag.OportunidadeId || ag.oportunidadeId;
        const nomeCliente = getNomeCliente(oportunidadeId);
        if (!nomeCliente.toLowerCase().includes(filtro.toLowerCase())) return false;
      }
    }
    // Filtro de atrasados
    if (mostrarAtrasados && !isAgendamentoAtrasado(ag)) return false;
    return true;
  });

  // Função para editar agendamento (busca dados da oportunidade pelo ID do card)
  const handleEditarAgendamento = async (agendamento) => {
    const oportunidadeId = agendamento.OportunidadeId || agendamento.oportunidadeId;
    if (!oportunidadeId) {
      alert('ID da Oportunidade não encontrado para este agendamento.');
      return;
    }
    try {
      const oportunidade = await apiCall(`Oportunidade/${oportunidadeId}`);
      setFormAgendamento({
        aberto: true,
        idAgendamento: agendamento.Id || agendamento.id,
        idCliente: oportunidade.clienteId || oportunidade.ClienteId,
        idOportunidade: oportunidade.id || oportunidade.Id,
        oportunidadeCompleta: oportunidade
      });
    } catch (e) {
      console.error('Erro ao buscar oportunidade:', e);
      alert('Erro ao buscar dados da oportunidade para edição.');
    }
  };

  return (
    <div className="container-geral fundo-branco">
      <div className="header-adicionar">
        <span className="texto-adicionar">Adicionar novo</span>
        <button className="botao-mais" onClick={() => setMostrarPopup(true)}>+</button>
      </div>

      {/* MobileFooter + button listener */}
      <MobileAddListener onOpen={() => setMostrarPopup(true)} />

      {/* Campo de filtro */}
      <div className="filtro-container">
        <div className="agendamentos-filtros-mobile">
          {/* Input de texto + dropdown Buscar por */}
          <div className="filtro-input-wrapper">
            <input
              type="text"
              className="filtro-input"
              placeholder={
                tipoFiltro === 'idAgendamento' ? 'ID do Agendamento...' :
                tipoFiltro === 'id' ? 'ID da Oportunidade...' :
                tipoFiltro === 'responsavel' ? 'Nome do responsável...' :
                'Nome do cliente...'
              }
              value={filtro}
              onChange={e => setFiltro(e.target.value)}
            />
            {filtro && (
              <button className="filtro-clear-button" onClick={() => setFiltro('')} title="Limpar filtro">×</button>
            )}
          </div>
          <div className="filtro-custom-select">
            <button
              className="filtro-custom-botao"
              onClick={() => { setDropdownFiltroAberto(!dropdownFiltroAberto); setDropdownTipoAberto(false); setDropdownAtrasadosAberto(false); }}
            >
              {tipoFiltro === 'nome' ? 'Nome' :
               tipoFiltro === 'idAgendamento' ? 'ID Agendamento' :
               tipoFiltro === 'id' ? 'ID Oportunidade' :
               tipoFiltro === 'responsavel' ? 'Responsável' : 'Nome'
              }
              <span className="seta-para-baixo">▼</span>
            </button>
            {dropdownFiltroAberto && (
              <div className="filtro-custom-opcoes">
                {[
                  { label: 'Nome', value: 'nome' },
                  { label: 'ID Agendamento', value: 'idAgendamento' },
                  { label: 'ID Oportunidade', value: 'id' },
                  { label: 'Responsável', value: 'responsavel' },
                ].map(op => (
                  <div key={op.value} onClick={() => { setTipoFiltro(op.value); setFiltro(''); setDropdownFiltroAberto(false); }}>
                    {op.label}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Dropdown por Tipo de Agendamento */}
          <div className="filtro-custom-select">
            <button
              className="filtro-custom-botao"
              onClick={() => { setDropdownTipoAberto(!dropdownTipoAberto); setDropdownFiltroAberto(false); setDropdownAtrasadosAberto(false); }}
            >
              {tipoAgendamentoFiltro
                ? getNomeTipoAgendamento(parseInt(tipoAgendamentoFiltro))
                : 'Todos os Tipos'
              }
              <span className="seta-para-baixo">▼</span>
            </button>
            {dropdownTipoAberto && (
              <div className="filtro-custom-opcoes">
                <div onClick={() => { setTipoAgendamentoFiltro(''); setDropdownTipoAberto(false); }}>Todos os Tipos</div>
                {tiposAgendamento.map(tipo => (
                  <div key={tipo.id || tipo.Id} onClick={() => { setTipoAgendamentoFiltro(String(tipo.id || tipo.Id)); setDropdownTipoAberto(false); }}>
                    {tipo.nome || tipo.Nome}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Todos ou só atrasados (lista de escolha, para nunca ficar ligado sem perceber) */}
          <div className="filtro-custom-select filtro-atrasados-select">
            <button
              className="filtro-custom-botao"
              onClick={() => { setDropdownAtrasadosAberto(!dropdownAtrasadosAberto); setDropdownTipoAberto(false); setDropdownFiltroAberto(false); }}
              title="Mostrar todos os agendamentos ou só os atrasados"
            >
              {mostrarAtrasados ? 'Agendamentos atrasados' : 'Todos os agendamentos'}
              <span className="seta-para-baixo">▼</span>
            </button>
            {dropdownAtrasadosAberto && (
              <div className="filtro-custom-opcoes">
                <div onClick={() => { setMostrarAtrasados(false); setDropdownAtrasadosAberto(false); }}>Todos os agendamentos</div>
                <div onClick={() => { setMostrarAtrasados(true); setDropdownAtrasadosAberto(false); }}>Agendamentos atrasados</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mensagem de erro de conexão */}
      {erroCarregamento && (
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <p style={{ color: '#c62828', fontWeight: 'bold', marginBottom: 8 }}>Erro ao conectar ao servidor</p>
          <p style={{ color: '#888', fontSize: 14, marginBottom: 16 }}>Verifique se o backend está rodando e recarregue a página.</p>
          <button
            onClick={() => window.location.reload()}
            style={{ padding: '10px 24px', background: 'rgb(235, 133, 0)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
          >
            Recarregar página
          </button>
        </div>
      )}

      {/* Lista de agendamentos filtrados */}
      <div className="lista-clientes">
        {agendamentosFiltrados.length === 0 && !erroCarregamento && (
          <div className="agendamento-listagem-vazio">
            Nenhum agendamento cadastrado.
          </div>
        )}
        {agendamentosFiltrados.map(ag => {
          let dataHoraFormatada = ag.DataHora || ag.dataHora;
          let dataFimFormatada = ag.DataFimAgenda || ag.dataFimAgenda;
          let data = '', horaInicio = '', horaFim = '';
          
          // Processar dataHora (início)
          if (dataHoraFormatada && dataHoraFormatada.includes('T')) {
            dataHoraFormatada = dataHoraFormatada.replace('T', ' ');
          }
          if (dataHoraFormatada) {
            [data, horaInicio] = dataHoraFormatada.split(' ');
            horaInicio = horaInicio ? horaInicio.substring(0, 5) : ''; // pegar apenas HH:MM
          }
          
          // Processar dataFimAgenda (fim)
          if (dataFimFormatada && dataFimFormatada.includes('T')) {
            dataFimFormatada = dataFimFormatada.replace('T', ' ');
          }
          if (dataFimFormatada) {
            const [, horaFimTemp] = dataFimFormatada.split(' ');
            horaFim = horaFimTemp ? horaFimTemp.substring(0, 5) : ''; // pegar apenas HH:MM
          }
          
          let prazoStatus = null;
          const status = ag.Status || ag.status;
          if (dataHoraFormatada) {
            const agendamentoDate = new Date(`${data}T${horaInicio || '00:00'}`);
            const now = new Date();
            const diffMs = agendamentoDate - now;
            const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
            if (status === 'A Visitar') {
              // Se já passou da data, mostramos o indicador principal (dot + botão + texto)
              // então evitamos duplicar o label "Atrasado" aqui.
              if (now > agendamentoDate) {
                prazoStatus = null;
              } else if (diffDays <= 5 && diffDays >= 0) {
                prazoStatus = (
                  <span className="agendamento-status agendamento-status-vencendo">
                    <span className="agendamento-status-bolinha agendamento-status-bolinha-amarela" />
                    <span className="agendamento-status-texto agendamento-status-texto-amarelo">Prazo vencendo</span>
                  </span>
                );
              }
            }
          }
          
          // Pegando o nome do cliente (agora direto do objeto Cliente do backend)
          const nomeCliente = getNomeClienteFromAgendamento(ag);
          
          // Pegando o telefone do cliente
          const clienteObj = ag.Cliente || ag.cliente;
          const idCliente = ag.Id_cliente || ag.id_cliente || (clienteObj && typeof clienteObj === 'object' ? (clienteObj.Id || clienteObj.id) : null);
          const telefoneCliente = (clienteObj && typeof clienteObj === 'object') 
            ? (clienteObj.Telefone || clienteObj.telefone || clienteObj.Celular || clienteObj.celular || 'Sem telefone')
            : getTelefoneCliente(idCliente);
          
          // Pegando o CPF/CNPJ do cliente
          const cpfCnpjCliente = (clienteObj && typeof clienteObj === 'object')
            ? (clienteObj.CpF_CNPJ || clienteObj.cpF_CNPJ || clienteObj.CpfCnpj || clienteObj.cpfCnpj || 'Sem CPF/CNPJ')
            : getCpfCnpjCliente(idCliente);
          
          // Formatando a data para o padrão brasileiro (dia/mês/ano)
          let dataFormatada = '';
          if (data) {
            const [ano, mes, dia] = data.split('-');
            dataFormatada = `${dia}/${mes}/${ano}`;
          }
          
          const agendamentoId = ag.Id || ag.id;
          const responsavel = getResponsavelFromAgendamento(ag);
          const statusDisplay = status || 'Sem status';

          return (
            <div className="card-cliente" key={agendamentoId} style={{ display: 'flex', borderBottom: '1px solid #ddd', padding: '10px 0' }}>
              <div style={{ width: '80%', display: 'flex', flexDirection: 'row', gap: '20px', padding: '10px 0' }}>
                {/* Coluna ID */}
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: '60px' }}>
                  <label style={{ fontWeight: 'bold', marginBottom: '5px' }}>ID</label>
                  <span>{agendamentoId}</span>
                </div>
                
                {/* Coluna ID Cliente */}
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: '80px' }}>
                  <label style={{ fontWeight: 'bold', marginBottom: '5px' }}>ID Cliente</label>
                  <span>{idCliente}</span>
                </div>
                
                {/* Coluna Cliente */}
                <div style={{ display: 'flex', flex: '0 1 140px', flexDirection: 'column', maxWidth: '140px', minWidth: '100px' }}>
                  <label style={{ fontWeight: 'bold', marginBottom: '5px' }}>Cliente</label>
                  <span title={nomeCliente} style={{ display: '-webkit-box', overflow: 'hidden', overflowWrap: 'anywhere', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2 }}>{nomeCliente}</span>
                </div>
                
                {/* Coluna CPF/CNPJ */}
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: '140px' }}>
                  <label style={{ fontWeight: 'bold', marginBottom: '5px' }}>CPF/CNPJ</label>
                  <span>{cpfCnpjCliente}</span>
                </div>
                
                {/* Coluna Telefone */}
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: '120px' }}>
                  <label style={{ fontWeight: 'bold', marginBottom: '5px' }}>Telefone</label>
                  <span>{telefoneCliente}</span>
                </div>
                
                {/* Coluna Responsável */}
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: '120px' }}>
                  <label style={{ fontWeight: 'bold', marginBottom: '5px' }}>Responsável</label>
                  <span>{responsavel}</span>
                </div>
                
                {/* Coluna Data/Hora - hora embaixo da data */}
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: '150px' }}>
                  <label style={{ fontWeight: 'bold', marginBottom: '5px' }}>Data/Hora</label>
                  <div style={{ display: 'flex', flexDirection: 'column', fontSize: '14px' }}>
                    <span style={{ fontWeight: '500' }}>{dataFormatada}</span>
                    <span style={{ color: '#666' }}>
                      {horaInicio && horaFim ? `${horaInicio} - ${horaFim}` : horaInicio}
                    </span>
                    {isAgendamentoAtrasado(ag) && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                        <span className="agendamento-atrasado-dot" aria-hidden="true" />
                        <span className="agendamento-atrasado-texto">Atrasado</span>
                      </div>
                    )}
                    {prazoStatus && <div style={{ marginTop: '4px' }}>{prazoStatus}</div>}
                  </div>
                </div>
                
                {/* Coluna Status */}
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: '100px' }}>
                  <label style={{ fontWeight: 'bold', marginBottom: '5px' }}>Status</label>
                  <span>{statusDisplay}</span>
                </div>
              </div>
              <div style={{ width: '20%', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                <button
                  className="botao-editar"
                  onClick={() => handleEditarAgendamento(ag)}
                  title="Editar agendamento"
                >
                  Editar
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Popup de clientes */}
      {mostrarPopup && (
        <div className="popup-agendamento-overlay" onClick={fecharTodosPopups}>
          <div className="popup-agendamento" onClick={e => e.stopPropagation()} style={{ position: 'relative' }}>
            <button
              className="popup-fechar-btn"
              onClick={() => {
                setMostrarPopup(false);
                setPopupOportunidades(false);
                setClienteSelecionado(null);
                setFormAgendamento({ aberto: false, idCliente: null, idOportunidade: null, agendamento: null });
              }}
              aria-label="Fechar"
            >
              ×
            </button>
            <div className="popup-agendamento-centro">
              <div className="popup-titulo-laranja">
                ESCOLHA UM CLIENTE PARA ADICIONAR
              </div>
              <div className="popup-lista-clientes">
                {clientes.length === 0 && (
                  <div className="popup-card-cliente vazio">Nenhum cliente cadastrado.</div>
                )}
                {clientes.map(cliente => (
                  <div className="popup-card-cliente" key={cliente.id || cliente.Id}>
                    <div className="popup-card-info">
                      <span className="popup-card-id">ID: {cliente.id || cliente.Id}</span>
                      <span className="popup-card-nome">{cliente.nome || cliente.Nome}</span>
                    </div>
                    <button
                      className="popup-card-novo-agendamento"
                      onClick={() => {
                        setClienteSelecionado(cliente);
                        setMostrarPopup(false);
                        setFormAgendamento({
                          aberto: true,
                          idCliente: cliente.id || cliente.Id,
                          idOportunidade: null,
                          agendamento: null
                        });
                      }}
                    >
                      Novo Agendamento
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Popup do formulário de agendamento */}
      {formAgendamento.aberto && (
        <div className="popup-agendamento-overlay" onClick={fecharTodosPopups}>
          <div className="popup-agendamento" onClick={e => e.stopPropagation()} style={{ position: 'relative' }}>
            {/* Botão Voltar - esquerda, fora do formulário branco, só para novos */}
            {!formAgendamento.idAgendamento && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setFormAgendamento({ aberto: false, idCliente: null, idOportunidade: null, agendamento: null }); setClienteSelecionado(null); setMostrarPopup(true); }}
                style={{
                  position: 'absolute',
                  top: '15px',
                  left: '15px',
                  width: '42px',
                  height: '32px',
                  backgroundColor: '#fff',
                  border: '1px solid rgb(235, 133, 0)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  zIndex: 10,
                  fontSize: '18px',
                  color: 'rgb(235, 133, 0)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
                }}
                title="Voltar"
              >
                ←
              </button>
            )}
            {/* Botão de fechar para todos os casos */}
            <button
              type="button"
              onClick={() => setFormAgendamento({ aberto: false, idAgendamento: null })}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                width: '35px',
                height: '35px',
                backgroundColor: 'rgb(235, 133, 0)',
                border: 'none',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 10,
                fontSize: '20px',
                color: '#fff',
                fontWeight: 'bold',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
              }}
              title="Fechar"
            >
              ×
            </button>
            
            <FormularioAgendamento
              idAgendamento={formAgendamento.idAgendamento}
              idCliente={formAgendamento.idCliente}
              idOportunidade={formAgendamento.idOportunidade}
              oportunidadeCompleta={formAgendamento.oportunidadeCompleta}
              isEdicao={!!formAgendamento.idAgendamento}
              mostrarBotaoVoltar={false}
              onClose={() => setFormAgendamento({ aberto: false, idAgendamento: null })}
              onVoltar={() => {
                setFormAgendamento({ aberto: false, idCliente: null, idOportunidade: null, agendamento: null });
                setClienteSelecionado(null);
                setMostrarPopup(true);
              }}
            />
          </div>
        </div>
      )}

      <LoadingOverlay isLoading={loading} message="Processando..." />
    </div>
  );
}